package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initXykPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("xyk.md", FrontMatterElement.Builder { addScalar("title", "xyk"); addScalar("searchTitle", "Durable webhook gateway in Kotlin/Native — Ktor, SQLite, one transaction per accepted webhook"); addScalar("description", "A durable webhook gateway in a very small container: the event row and the timer that will deliver it are committed in one transaction, so an accepted webhook cannot go missing. Kotlin/Native, Ktor and SQLite, measured against criteria declared before the code."); addScalar("library", "xyk"); }))
}

@Page("/xyk")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun XykPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A webhook receiver has one job that is easy to state and easy to get wrong: once it has said ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("200")
        }
        org.jetbrains.compose.web.dom.Text(", the event must not be lost. Most receivers answer first and persist second, or persist the event and schedule its delivery as two separate writes — and the gap between them is exactly where a restart loses a payment notification. ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/xyk") {
            org.jetbrains.compose.web.dom.Text("xyk")
        }
        org.jetbrains.compose.web.dom.Text(" accepts a webhook from anyone who can POST — GitHub, Telegram, Stripe — proves it is genuine, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("writes it down before answering")
        }
        org.jetbrains.compose.web.dom.Text(", and delivers it to every subscriber with retries, backoff and per-attempt timeouts. The event row and the timer that will deliver it are committed in ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("one transaction")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It is written in Kotlin/Native on Ktor with SQLite for a second reason: to find out, against numbers declared before the code, whether that platform can hold the promise inside a container small enough to run one per project. The same scenarios are measured against a Go twin of the ingest path, and the two criteria it misses are written down as missed rather than restated to fit.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Signature verification over the exact bytes")
            }
            org.jetbrains.compose.web.dom.Text(" — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("X-Hub-Signature-256")
            }
            org.jetbrains.compose.web.dom.Text(" the way GitHub sends it; the same request without the header is ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("401")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Delivery to every subscriber")
            }
            org.jetbrains.compose.web.dom.Text(" with retries, backoff and a timeout per attempt, from four workers by default — a number measured rather than guessed.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A journal page")
            }
            org.jetbrains.compose.web.dom.Text(" at ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("/journal")
            }
            org.jetbrains.compose.web.dom.Text(" showing what arrived and what happened to it, attempt by attempt, and the same thing as JSON at ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("GET /api/events")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Refusal over defaults")
            }
            org.jetbrains.compose.web.dom.Text(": a value the service cannot read — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("XYK_MAX_BODY_BYTES=banana")
            }
            org.jetbrains.compose.web.dom.Text(" — prints one line naming the variable and exits ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("78")
            }
            org.jetbrains.compose.web.dom.Text(", instead of starting on a number nobody chose.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A cold start of 0.394 s")
            }
            org.jetbrains.compose.web.dom.Text(" to the first ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("200")
            }
            org.jetbrains.compose.web.dom.Text(" on a k0s node, indistinguishable from the Go twin's 0.395.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""docker run -d --name xyk -p 8080:8080 -v xyk-data:/data \
  -e XYK_BOOTSTRAP_ENDPOINT_ID=hook-1 \
  -e XYK_BOOTSTRAP_SECRET=a-secret-you-choose \
  ghcr.io/youndie/xyk:main
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("main")
        }
        org.jetbrains.compose.web.dom.Text(" moves; ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sha-<commit>")
        }
        org.jetbrains.compose.web.dom.Text(" does not — deploy the second. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("XYK_DB_PATH")
        }
        org.jetbrains.compose.web.dom.Text(" is the only required variable and the image sets it to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/data/xyk.db")
        }
        org.jetbrains.compose.web.dom.Text(": without a volume there, every accepted and not yet delivered webhook is lost when the container is replaced. The volume is as sensitive as the secrets in it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A Helm chart ships under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("charts/xyk")
        }
        org.jetbrains.compose.web.dom.Text(". It takes the bootstrap secret as an existing ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Secret")
        }
        org.jetbrains.compose.web.dom.Text(" rather than a value — a secret in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("values.yaml")
        }
        org.jetbrains.compose.web.dom.Text(" is a secret in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("helm history")
        }
        org.jetbrains.compose.web.dom.Text(", and neither forgets — uses ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Recreate")
        }
        org.jetbrains.compose.web.dom.Text(" rather than a rolling update because one SQLite file has one writer, and keeps the PVC when the release is removed.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-criteria-and-the-answers") }) {
        org.jetbrains.compose.web.dom.Text("The criteria, and the answers")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Criterion")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Answer")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("2 000 rps on ingest over 200 connections, no slow state")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("No.")
                    }
                    org.jetbrains.compose.web.dom.Text(" 437 rps on four visible cores; the Go twin misses it too, at 601")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("64 MiB limit, ten runs out of ten")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("Yes for thirty seconds at a time, and no longer")
                    }
                    org.jetbrains.compose.web.dom.Text(" — see the first limitation")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("image at most 10 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("No")
                    }
                    org.jetbrains.compose.web.dom.Text(", by 1.74 MiB, on purpose: the excess is the charset converters without which the journal page returns ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("500")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("cold start under a second on a k0s node")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("Yes")
                    }
                    org.jetbrains.compose.web.dom.Text(", 0.394 s")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("if curl makes a static link impossible, that is a result")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("not triggered — all four link modes link; curl costs 8.8 MB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("ktor-client-curl")
                }
                org.jetbrains.compose.web.dom.Text(" grows about 2 kB per request")
            }
            org.jetbrains.compose.web.dom.Text(", so a memory limit is a time budget rather than a margin: 64 MiB is roughly 90 s at 60 rps, 128 MiB roughly four minutes. It reproduces with one client and no service around it, and the CIO engine is flat — but CIO does not speak TLS on Kotlin/Native, and curl is the only engine that does. Closed as ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("dropped")
            }
            org.jetbrains.compose.web.dom.Text(", not fixed, so the tick does not read as a repair.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Outbound HTTPS is a build variant. The published image has it; a build without it accepts and journals webhooks and says at start-up that it will not deliver them.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One process, one SQLite file, one writer. The chart's ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Recreate")
            }
            org.jetbrains.compose.web.dom.Text(" strategy is that fact spelled out; there is no clustering story.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The Helm chart was validated with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--dry-run=server")
            }
            org.jetbrains.compose.web.dom.Text(" against a live k0s API server and the container was run with exactly the environment it renders — a full ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("helm install")
            }
            org.jetbrains.compose.web.dom.Text(" was ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("not")
            }
            org.jetbrains.compose.web.dom.Text(" completed, because the test node's CNI could not give pods an address. Validated, not deployed.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("No releases: the image is published as ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("main")
            }
            org.jetbrains.compose.web.dom.Text(" and as ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("sha-<commit>")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
}
